import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../auth/session';
import { LegalLinks } from '../components/LegalLinks';
import { initials } from '../lib/format';
import type { Me } from '../types';
import type { Tarif } from './types';
import './kopf.css';

// Billtoro: Konto-Menü rechts in der Kopfzeile des Rechnungswerkzeugs (statt „Abmelden“): Konto, Tarif, Team,
// Steuerbüro, weitere Firma, Abmelden und die rechtlichen Seiten. Muster: UserMenu der Mandanten-App
// (client/ClientLayout.tsx). Das Steuerbüro richtet nur der Inhaber ein; ein Team-Mitglied sieht es nicht, auch nicht,
// solange sein Zugang ruht und die Übersicht deshalb fehlt (ruht; Team schon: wer mitarbeitet, Firma verlassen).
// „Ihre Mandanten“ nur mit Zugang fürs Steuerbüro (me.buero), „Betreiber-Bereich“ nur für den Betreiber
// (me.betreiber); ohne laufende Firma kein Tarif, Team und Steuerbüro.
// Auch in der Kopfzeile von /mandanten (mandanten/MandantenBereich.tsx, ohne Tarif).
// Bedienung wie ein Menü: Pfeiltasten, Pos1/Ende, Escape, Tab verlässt es.
export function KontoMenue({ me, tarif, ruht }: { me: Me; tarif?: Tarif | null; ruht?: boolean }) {
  const { logout } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const mitglied = tarif?.rolle === 'mitglied' || !!ruht;
  const firma = me.clients.some((c) => c.status === 'active');
  const eintraege: [string, string][] = [
    ['/rechnung/konto', 'Mein Konto'],
    ...(me.buero ? ([['/mandanten', 'Ihre Mandanten']] as [string, string][]) : []),
    ...(me.betreiber ? ([['/betreiber', 'Betreiber-Bereich']] as [string, string][]) : []),
    ...(firma
      ? ([
          ['/rechnung/tarif', 'Tarif & Bezahlung'],
          ['/rechnung/team', 'Team'],
          ...(mitglied ? [] : [['/rechnung/steuerbuero', 'Steuerbüro']]),
          ['/rechnung/firma-neu', 'Weitere Firma anlegen'],
        ] as [string, string][])
      : ([['/rechnung/firma-neu', 'Firma anlegen']] as [string, string][])),
  ];

  useEffect(() => {
    if (!open) return;
    // erster Eintrag bekommt den Fokus (Bedienung mit Tastatur und Bildschirmleser)
    liste.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    // Klick daneben oder Fokus landet woanders: zu. Nicht schon beim Verlassen (blur): Safari gibt Knöpfen und Links
    // beim Klick keinen Fokus, der Fokus geht beim Drücken der Maus auf die Seite, und das Menü wäre vor dem Klick zu.
    const close = (e: MouseEvent | FocusEvent | globalThis.KeyboardEvent) => {
      if (e instanceof globalThis.KeyboardEvent) {
        if (e.key !== 'Escape') return;
        setOpen(false);
        knopf.current?.focus();
      } else if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('focusin', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('focusin', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  function tasten(e: KeyboardEvent<HTMLDivElement>) {
    // Tab und Umschalt+Tab: zu, der Fokus geht vom Knopf aus weiter (auch, wenn danach auf der Seite nichts mehr kommt)
    if (e.key === 'Tab') {
      knopf.current?.focus();
      setOpen(false);
      return;
    }
    const items = [...(liste.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    const ziel =
      e.key === 'ArrowDown' ? (i + 1) % items.length
      : e.key === 'ArrowUp' ? (i - 1 + items.length) % items.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? items.length - 1
      : -1;
    if (ziel < 0) return;
    e.preventDefault();
    items[ziel]?.focus();
  }

  return (
    <div className="bt-menue" ref={ref}>
      <button
        ref={knopf}
        type="button"
        className="bt-menue-knopf"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls="bt-menue-liste"
        aria-label={`Konto von ${me.user.name}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <i aria-hidden="true">{initials(me.user.name)}</i>
      </button>
      {open && (
        <div
          className="bt-menue-liste"
          id="bt-menue-liste"
          role="menu"
          aria-label="Konto"
          ref={liste}
          onKeyDown={tasten}
          // Eintrag gewählt (auch Impressum, Datenschutz und AGB in einem neuen Tab): zu
          onClick={(e) => {
            if ((e.target as Element).closest('[role="menuitem"]')) setOpen(false);
          }}
        >
          <div className="bt-menue-wer" role="none">
            <b>{me.user.name}</b>
            <small>{me.user.email}</small>
          </div>
          {eintraege.map(([to, label]) => (
            <Link key={to} to={to} role="menuitem">
              {label}
            </Link>
          ))}
          <button type="button" role="menuitem" className="bt-menue-ab" onClick={() => void logout()}>
            Abmelden
          </button>
          <LegalLinks menu mitAgb />
        </div>
      )}
    </div>
  );
}
